<script setup lang="ts">
import Modal from '@/components/common/layout/Modal.vue'
import { useToast } from 'vue-toast-notification'
import { storeToRefs } from 'pinia'
import SaveButton from '@/components/common/buttons/SaveButton.vue'
import { useVariableStore, type Variable } from '@/stores/useVariableStore'
import NumberInput from '@/components/common/inputs/NumberInput.vue'

const emit = defineEmits(['close'])
const props = defineProps({
  variable: {
    type: Object as () => Variable,
    required: true,
  },
  showModal: {
    type: Boolean,
    default: false,
    required: true,
  },
  readonlyAdjustments: {
    type: Boolean,
    default: false,
  },
})
const $toast = useToast()

const variableStore = useVariableStore()
const { loadingUpdate } = storeToRefs(variableStore)

const onSubmit = async () => {
  const variableDto = {
    uuid: props.variable.id,
    recovery: props.variable.recovery,
    complement: props.variable.complement,
  }

  await variableStore
    .updateVariable(variableDto)
    .then(() => {
      variableStore.fetchMyVariables()
      $toast.success('Variable mis à jour avec succès.')
      emit('close')
    })
    .catch((error) => {
      $toast.error(error.message)
    })
}
</script>

<template>
  <Modal
    :showModal="showModal"
    @close="emit('close')"
    :title="`Edition du variable ${new Date(variable.period).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })} - ${variable.salesEngineer}`"
    :loading="loadingUpdate"
  >
    <template #content>
      <form @submit.prevent="onSubmit" class="space-y-6">
        <!-- Informations du variable en lecture seule -->
        <div class="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <h3 class="text-lg font-semibold text-gray-800 mb-4">Détails du variable</h3>
          <div class="grid grid-cols-2 gap-4">
            <!-- Marge brute -->
            <div class="bg-white rounded-lg p-4 border border-gray-200">
              <div class="text-xs text-gray-500 uppercase tracking-wider mb-1">Marge brute</div>
              <div class="text-2xl font-bold text-gray-900">{{ variable.totalCounterCommission?.toLocaleString('fr-FR') || 0 }} €</div>
            </div>

            <!-- Taux atteint -->
            <div class="bg-white rounded-lg p-4 border border-gray-200">
              <div class="text-xs text-gray-500 uppercase tracking-wider mb-1">Taux atteint</div>
              <div class="text-2xl font-bold text-gray-900">{{ variable.reachedRate || 0 }}%</div>
            </div>

            <!-- Variable de base -->
            <div class="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div class="text-xs text-blue-700 uppercase tracking-wider mb-1 font-semibold">Variable de base</div>
              <div class="text-2xl font-bold text-blue-900">{{ variable.variable?.toLocaleString('fr-FR') || 0 }} €</div>
              <div class="text-xs text-blue-600 mt-1">
                {{ variable.totalCounterCommission?.toLocaleString('fr-FR') || 0 }}€ × {{ variable.reachedRate || 0 }}%
              </div>
            </div>

            <!-- Total actuel -->
            <div class="bg-orange-50 rounded-lg p-4 border border-orange-200">
              <div class="text-xs text-orange-700 uppercase tracking-wider mb-1 font-semibold">Total variable</div>
              <div class="text-2xl font-bold text-orange-900">{{ variable.amount?.toLocaleString('fr-FR') || 0 }} €</div>
            </div>
          </div>
        </div>

        <!-- Champs éditables -->
        <div class="bg-white rounded-lg p-6 border border-gray-200">
          <h3 class="text-lg font-semibold text-gray-800 mb-4">Ajustements</h3>
          <div class="grid grid-cols-2 gap-4">
            <NumberInput v-model="variable.recovery" label="Reprise" placeholder="Montant de reprise (positif ou négatif)" :min="-999999" step="0.01" :disabled="readonlyAdjustments" />
            <NumberInput v-model="variable.complement" label="Complément" placeholder="Montant de complément" step="0.01" :disabled="readonlyAdjustments" />
          </div>
          <p v-if="readonlyAdjustments" class="mt-2 text-xs text-warning flex items-center gap-1">
            ⚠ Reprise et complément non modifiables avec votre rôle.
          </p>
          <div class="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div class="text-sm text-amber-800">
              <span class="font-semibold">Nouveau total calculé :</span>
              {{ ((variable.variable || 0) + (variable.recovery || 0) + (variable.complement || 0)).toLocaleString('fr-FR') }} €
            </div>
          </div>
        </div>

        <SaveButton :loading="loadingUpdate" :disabled="readonlyAdjustments" />
      </form>
    </template>
  </Modal>
</template>

<style>
.ql-editor {
  height: 125px !important;
}
</style>
